import './style.css';

import type { GameState, TileState } from './types';
import { COLS, DRIP_MS, ONBOARDED_KEY, ROWS } from './game/constants';
import { evaluateGuess } from './game/evaluate';
import { loadPuzzle } from './game/puzzle';
import { createState } from './game/state';
import { load, migrateLegacyStorage, save } from './game/persistence';
import { loadCatalog } from './data/catalog';
import { buildValidGuesses } from './data/validGuesses';

import { $ } from './ui/dom';
import { buildBoard, revealRow, renderBoard, shakeRow } from './ui/board';
import { buildKeyboard, renderKeyboard, type KeyInput } from './ui/keyboard';
import {
  accelerateDrip,
  cancelDrip,
  isDripRunning,
  pauseDrip,
  resumeDrip,
  startRowDrip,
} from './ui/drip';
import { installFlipHandler, renderMovie } from './ui/vhsBox';
import { closeModal, openModal, restoreHelp, showResult, snapshotHelp } from './ui/modal';
import { showToast } from './ui/toast';
import { installEasterEggs } from './ui/easterEggs';

// Konami listener must beat the main keydown handler installed below — install
// it first so its `stopImmediatePropagation` works on the B/A keys.
installEasterEggs();

// Register the PWA service worker after load so it never competes with game
// bootstrap. Skipped in dev — `localhost` is a secure context, so without
// this guard the SW caches /src/* and serves stale CSS/JS across restarts.
// In dev we also actively unregister any SW left over from a previous prod
// build or earlier dev session; otherwise stale caches persist until manual
// devtools cleanup.
if ('serviceWorker' in navigator) {
  if (import.meta.env.PROD && window.isSecureContext) {
    window.addEventListener('load', () => {
      void navigator.serviceWorker.register('/sw.js');
    });
  } else {
    void navigator.serviceWorker.getRegistrations().then((regs) => {
      for (const reg of regs) void reg.unregister();
    });
    if ('caches' in window) {
      void caches.keys().then((keys) => {
        for (const k of keys) void caches.delete(k);
      });
    }
  }
}

// Expose state for DevTools poking. Kept identical to the legacy `window.state`
// so existing muscle memory still works.
declare global {
  interface Window {
    state?: GameState;
  }
}

void main();

async function main(): Promise<void> {
  migrateLegacyStorage();

  const catalog = await loadCatalog();
  const validGuesses = buildValidGuesses(catalog);
  const puzzle = await loadPuzzle(catalog);
  const state = createState(puzzle);
  window.state = state;

  const boardEl = $('board');
  const kbEl = $('keyboard');
  const modalEl = $('modal-backdrop');

  buildBoard(boardEl);
  buildKeyboard(kbEl, handleKey);
  renderMovie(state.puzzle);
  snapshotHelp();

  const saved = load(state.puzzle.dayIndex);
  if (saved) {
    state.guesses = saved.guesses;
    state.status = saved.status;
    state.chillMode = saved.chillMode;
    state.dripStartedAt = saved.dripStartedAt;
  }

  renderBoard(boardEl, state);
  renderKeyboard(kbEl, state);

  // First-run onboarding: auto-open the help modal once per device, but only
  // when the player has no save in progress. The result-modal path (below)
  // takes priority for completed rounds.
  let onboarded = false;
  try {
    onboarded = localStorage.getItem(ONBOARDED_KEY) === '1';
  } catch {
    onboarded = false;
  }
  const isFirstRun = !onboarded && !saved && state.status === 'playing';
  if (isFirstRun) {
    openModal();
    try {
      localStorage.setItem(ONBOARDED_KEY, '1');
    } catch {
      // ignore
    }
  }

  if (state.status === 'won' || state.status === 'lost') {
    showResult(state);
  } else {
    maybeStartActiveRowDrip();
  }

  document.addEventListener('keydown', (e) => {
    if (modalEl.hidden === false && e.key === 'Escape') {
      handleModalClose();
      return;
    }
    if (document.body.classList.contains('movie-open') && e.key === 'Escape') {
      closeMovie();
      return;
    }
    // Once the game is over, only Escape is honored. Konami's own listener is
    // separate and stays active.
    if (state.status !== 'playing') return;
    if (e.key === 'Enter') handleKey('ENTER');
    else if (e.key === 'Backspace') handleKey('BACK');
    else if (/^[a-zA-Z]$/.test(e.key)) handleKey(e.key.toUpperCase());
  });

  $('help-btn').addEventListener('click', () => {
    pauseDrip();
    restoreHelp();
    openModal();
  });
  $('modal-close').addEventListener('click', handleModalClose);
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) handleModalClose();
  });

  // Mobile-only movie button: lifts the VHS box into a centered overlay.
  // We physically move the node so it escapes <main>'s stacking context
  // (z-index: 5) and sits above the overlay backdrop. JS-only — no clones —
  // so the 3D flip handler stays attached to the same element. The narration
  // line stays inline at the top of <main>; closing/opening the overlay no
  // longer steals it from the player.
  const vhsBox = $('vhs-box');
  const movieOverlay = $('movie-overlay');
  const vhsHome = vhsBox.parentElement;
  const closeMovie = (): void => {
    if (vhsHome && vhsBox.parentElement === movieOverlay) {
      // appendChild so the box drops back in after the rental-label sibling
      // instead of being inserted before it.
      vhsHome.appendChild(vhsBox);
    }
    document.body.classList.remove('movie-open');
    movieOverlay.hidden = true;
  };
  $('movie-btn').addEventListener('click', () => {
    movieOverlay.appendChild(vhsBox);
    document.body.classList.add('movie-open');
    movieOverlay.hidden = false;
  });
  movieOverlay.addEventListener('click', (e) => {
    // Only dismiss when the player taps the backdrop itself, not the contents.
    if (e.target === movieOverlay) closeMovie();
  });

  installFlipHandler();

  function handleModalClose(): void {
    closeModal();
    // If the drip was paused while the modal was open, resume it. Otherwise
    // start it (first-run path: modal closed for the first time, no drip yet).
    if (isDripRunning()) resumeDrip();
    else maybeStartActiveRowDrip();
  }

  function maybeStartActiveRowDrip(): void {
    if (state.status !== 'playing') return;
    if (state.chillMode) return;
    if (modalEl.hidden === false) return;
    const rowIdx = state.guesses.length;
    if (rowIdx >= ROWS) return;
    const duration = DRIP_MS[rowIdx];
    if (duration === undefined) return;
    if (state.dripStartedAt === null) {
      state.dripStartedAt = Date.now();
      save(state);
    }
    const elapsed = Math.max(0, Date.now() - state.dripStartedAt);
    startRowDrip(boardEl, rowIdx, duration, elapsed, drain);
  }

  function handleKey(k: KeyInput): void {
    if (state.status !== 'playing') return;
    if (k === 'ENTER') {
      submitGuess();
      return;
    }
    if (k === 'BACK') {
      state.current = state.current.slice(0, -1);
      renderBoard(boardEl, state);
      return;
    }
    if (/^[A-Z]$/.test(k) && state.current.length < COLS) {
      state.current += k;
      renderBoard(boardEl, state);
    }
  }

  function submitGuess(): void {
    if (state.current.length !== COLS) {
      shakeRow(boardEl, state.guesses.length);
      showToast('Need 5 letters');
      return;
    }
    if (!validGuesses.has(state.current)) {
      shakeRow(boardEl, state.guesses.length);
      showToast("Not in tonight's script");
      // Clear the row after the shake so the player can immediately retype.
      setTimeout(() => {
        state.current = '';
        renderBoard(boardEl, state);
      }, 600);
      return;
    }
    const word = state.current;
    state.current = '';
    const results = evaluateGuess(word, state.puzzle.word);
    recordGuess(word, results);
  }

  // Auto-submit the active row as a wrong guess. Called from the drip
  // controller when the killer's blood reaches the bottom of the row. The
  // typed letters (if any) stay on the board as a record of "you didn't
  // make it" — but all five tiles are absent.
  function drain(): void {
    if (state.status !== 'playing') return;
    const word = state.current;
    state.current = '';
    const results: TileState[] = ['absent', 'absent', 'absent', 'absent', 'absent'];
    recordGuess(word, results);
  }

  function recordGuess(word: string, results: readonly TileState[]): void {
    const rowIdx = state.guesses.length;
    state.guesses.push({ word, results });

    // Lock end-of-game status BEFORE the flip animation so no input leaks onto
    // the next row during the reveal. A drained row can never be a win, so
    // only check isOut on the drain path — but a unified isWin/isOut check
    // covers both submit and drain without branching.
    const isWin = results.every((r) => r === 'correct');
    const isOut = state.guesses.length >= ROWS;
    if (isWin) state.status = 'won';
    else if (isOut) state.status = 'lost';
    if (isWin || isOut) renderKeyboard(kbEl, state);

    // If a drip is still descending (player submitted before drain), rush
    // it to completion from its current position rather than cancelling —
    // cancelling would let .absent's 2.8s curtain restart from the top.
    // Otherwise (timer-driven drain), normal cancel. Either way, the
    // dripStartedAt timestamp clears here; the next row's drip starts fresh.
    if (isDripRunning()) accelerateDrip();
    else cancelDrip();
    state.dripStartedAt = null;

    revealRow(boardEl, rowIdx, results);

    setTimeout(
      () => {
        renderBoard(boardEl, state);
        renderKeyboard(kbEl, state);
        save(state);

        if (state.status === 'won' || state.status === 'lost') {
          setTimeout(() => showResult(state), 800);
        } else {
          maybeStartActiveRowDrip();
        }
      },
      600 + COLS * 100,
    );
  }
}
